/**
 * Subtopic papers: one subtopic of a section (UCAT-4001-s3, TMUA-4001-p1).
 *
 * They are their own category, ids in the 4000 range, and a tier of foundations
 * or beyond. Standard papers are foundations and harder papers are beyond. They
 * are never a mock and never a section paper, whatever the tier says.
 */

import { topicAttemptRowFromMap, topicRowAction } from './topicAttemptRow';

export const SUBTOPIC_PAPERS_CATEGORY = 'subtopic_papers';
export const SUBTOPIC_PAPERS_LABEL = 'Practice subtopics';
export const SUBTOPIC_PAPERS_ALL_PAGE = 'subtopics';
export const SUBTOPIC_PAPER_DEFAULT_TIER = 'foundations';
export const SUBTOPIC_PAPER_TIERS = ['beginnings', 'foundations', 'beyond'];

export const SUBTOPIC_TIER_PAGES = [
  { page: 'subtopics-beginnings', tier: 'beginnings', label: 'Beginnings', level: 1 },
  { page: 'subtopics-foundations', tier: 'foundations', label: 'Foundations', level: 2 },
  { page: 'subtopics-beyond', tier: 'beyond', label: 'Beyond', level: 3 },
];

export const SUBTOPIC_PAGE_TITLES = {
  [SUBTOPIC_PAPERS_ALL_PAGE]: SUBTOPIC_PAPERS_LABEL,
  ...Object.fromEntries(
    SUBTOPIC_TIER_PAGES.map(({ page, label }) => [page, `${SUBTOPIC_PAPERS_LABEL}: ${label}`])
  ),
};

export function isSubtopicPaperEntry(paper) {
  return String(paper?.category || '').trim().toLowerCase() === SUBTOPIC_PAPERS_CATEGORY;
}

function paperKey(paper) {
  return String(paper?.id || paper?.catalog_id || '').trim().toLowerCase();
}

export function subtopicPaperTitle(paper) {
  return String(paper?.display_name || paper?.title || paper?.id || paper?.catalog_id || '').trim();
}

/** "Decision Making" from "Decision Making: Venn Diagrams 1". */
export function subtopicPaperGroupLabel(paper) {
  const title = subtopicPaperTitle(paper);
  const at = title.indexOf(':');
  return at > 0 ? title.slice(0, at).trim() : '';
}

function paperNumber(paper) {
  const match = subtopicPaperTitle(paper).match(/(\d+)\s*(?:\(harder\))?\s*$/i);
  if (match) return parseInt(match[1], 10);
  const fromId = paperKey(paper).match(/-(\d{4})-/);
  return fromId ? parseInt(fromId[1], 10) : Number.MAX_SAFE_INTEGER;
}

function idSuffix(paper) {
  const match = paperKey(paper).match(/-([a-z0-9]+)$/);
  return match ? match[1] : '';
}

export function subtopicPaperTier(paper) {
  const tier = String(paper?.tier || '').trim().toLowerCase();
  return SUBTOPIC_PAPER_TIERS.includes(tier) ? tier : SUBTOPIC_PAPER_DEFAULT_TIER;
}

export function subtopicPapersOfTier(papers, tier) {
  return (papers || []).filter((paper) => subtopicPaperTier(paper) === tier);
}

export function sortSubtopicPapers(papers) {
  return [...(papers || [])].sort((a, b) => {
    const section = subtopicPaperGroupLabel(a).localeCompare(subtopicPaperGroupLabel(b));
    if (section !== 0) return section;
    const suffix = idSuffix(a).localeCompare(idSuffix(b), undefined, { numeric: true });
    if (suffix !== 0) return suffix;
    return paperNumber(a) - paperNumber(b);
  });
}

export function selectSubtopicPapers(papers, format = (paper) => paper) {
  const own = (Array.isArray(papers) ? papers : []).filter(isSubtopicPaperEntry);
  return sortSubtopicPapers(own.map(format));
}

/** The same completion figures a section paper uses, over these papers. */
export function subtopicPaperProgress(papers, dbPapers = {}, completedList = []) {
  const list = papers || [];
  let done = 0;
  const percents = [];
  list.forEach((paper) => {
    const row = topicAttemptRowFromMap(dbPapers, paper.id);
    if (topicRowAction(row, completedList.includes(paper.id)).status !== 'done') return;
    done += 1;
    const total = Number(row?.score_total) || Number(row?.score_answered) || 0;
    const correct = Number(row?.score_correct);
    if (total > 0 && Number.isFinite(correct)) percents.push((correct / total) * 100);
  });
  return {
    done,
    total: list.length,
    pct: list.length ? Math.round((done / list.length) * 100) : 0,
    score: percents.length ? Math.round(percents.reduce((a, b) => a + b, 0) / percents.length) : null,
  };
}
